import {
  Box,
  Button,
  CircularProgress,
  Icon,
  Theme,
  Tooltip,
  Typography,
} from "@outschool/backpack";
import { faLockRegular, faUnlock } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import { navigate } from "@patched/hookrouter";
import React, { useMemo, useState } from "react";

import HeroLayoutWithTabs from "../../layout/HeroLayoutWithTabs/HeroLayoutWithTabs";
import useAnalyticsPage, {
  PageType,
} from "../../lib/analytics/useAnalyticsPage";
import { learnerRoutes } from "../../lib/Routes";
import { QUIZ_WRITER_CARD_BACKGROUND_GRADIENT } from "../Quiz/consts";
import { useQuizWriterTopicSuggestions } from "../Quiz/useQuizWriterTopicSuggestions";

export default function LearnerPlay() {
  const { t } = useTranslation("learnerApp\\LearnerPlay");
  useAnalyticsPage({ pageName: PageType.Discovery });
  const isQuizEnabled = useFeatureFlag("aic-quiz");

  const currentPath = isQuizEnabled
    ? learnerRoutes.playPath()
    : learnerRoutes.discoveryPath();

  return (
    <HeroLayoutWithTabs currentPath={currentPath}>
      <Box
        flex
        sx={{
          flexDirection: "column",
          gap: "16px",
          marginBottom: "4em",
        }}
      >
        <Typography variant="h2">
          {t("How would you like to play today?")}
        </Typography>
        <Typography variant="body1" sx={{ color: "grey.600" }}>
          {t("Start with a topic for a quiz, or a surprise idea.")}
        </Typography>

        <Box
          flex
          sx={(theme: Theme) => ({
            flexDirection: "column",
            gap: "20px",
            marginTop: "8px",
            [theme.breakpoints.up("md")]: {
              gap: "24px",
            },
          })}
        >
          {isQuizEnabled && <QuizWriterCard />}

          <MixupMachineCard />
        </Box>
      </Box>
    </HeroLayoutWithTabs>
  );
}

const UNLOCKED_TOPIC_SUGGESTION_BG = "hsl(44 100% 55% / 0.25)";
const UNLOCKED_TOPIC_SUGGESTION_BG_HOVER = "hsl(44 100% 55% / 0.4)";
const UNLOCKED_TOPIC_SUGGESTION_BORDER = "hsl(44 100% 55%)";

function QuizWriterCard() {
  const { t } = useTranslation("learnerApp\\LearnerPlay");
  const { topics, loading: topicsLoading } = useQuizWriterTopicSuggestions();
  const [isUnlockableTopicTooltipOpen, setIsUnlockableTopicTooltipOpen] =
    useState(false);

  const { unlockedTopics, otherTopics } = useMemo(
    () => ({
      unlockedTopics: topics.filter(topic => topic.showAsUnlocked),
      otherTopics: topics.filter(topic => !topic.showAsUnlocked),
    }),
    [topics],
  );

  return (
    <Box
      sx={(theme: Theme) => ({
        background: QUIZ_WRITER_CARD_BACKGROUND_GRADIENT,
        borderRadius: "16px",
        padding: "28px 32px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        [theme.breakpoints.down("sm")]: {
          padding: "20px 24px",
        },
      })}
    >
      <Box flex sx={{ alignItems: "center", gap: "16px" }}>
        <Box
          flex
          sx={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            flexShrink: 0,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "white" }}
            data-lov-id="src/pages/ExploreLandingV2Original.tsx:125:14"
            data-lov-name="PenLine"
            data-component-path="src/pages/ExploreLandingV2Original.tsx"
            data-component-line="125"
            data-component-file="ExploreLandingV2Original.tsx"
            data-component-name="PenLine"
            data-component-content="%7B%22className%22%3A%22w-5%20h-5%20text-white%22%7D"
          >
            <path d="M12 20h9"></path>
            <path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z"></path>
          </svg>
        </Box>
        <Box flex sx={{ flexDirection: "column", gap: "4px" }}>
          <Typography
            variant="h4"
            sx={{ color: "common.white", fontWeight: 700 }}
          >
            {t("Quiz Writer")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255, 255, 255, 0.85)" }}
          >
            {t("Start with a topic and create a quiz to explore it.")}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          minHeight: topicsLoading ? 36 : undefined,
        }}
      >
        {!topicsLoading && unlockedTopics.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
              width: "100%",
            }}
          >
            <Box
              flex
              sx={{
                alignItems: "center",
                gap: "8px",
                flexShrink: 0,
              }}
            >
              <Icon
                icon={faUnlock}
                sx={{
                  fontSize: "1.35rem",
                  color: "rgba(255, 255, 255, 0.95)",
                }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "1.35rem",
                  color: "rgba(255, 255, 255, 0.9)",
                }}
              >
                {t("Unlocked")}
              </Typography>
            </Box>
            {unlockedTopics.map(topic => (
              <Box
                key={topic.uid}
                component="button"
                type="button"
                onClick={() =>
                  navigate(learnerRoutes.quizPath(), false, {
                    topicUid: topic.uid,
                  })
                }
                sx={{
                  fontWeight: 700,
                  borderRadius: "999px",
                  border: `2px solid ${UNLOCKED_TOPIC_SUGGESTION_BORDER}`,
                  backgroundColor: UNLOCKED_TOPIC_SUGGESTION_BG,
                  color: "common.white",
                  padding: "5px 14px",
                  fontSize: "1.4rem",
                  cursor: "pointer",
                  userSelect: "none",
                  fontFamily: "inherit",
                  height: "38px",
                  "&:hover": {
                    border: `2px solid ${UNLOCKED_TOPIC_SUGGESTION_BORDER}`,
                    backgroundColor: UNLOCKED_TOPIC_SUGGESTION_BG_HOVER,
                  },
                }}
              >
                {topic.name}
              </Box>
            ))}
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "1.35rem",
              color: "rgba(255, 255, 255, 0.9)",
              marginRight: "4px",
            }}
          >
            {t("Try")}
          </Typography>
          {topicsLoading ? (
            <CircularProgress
              size={24}
              sx={{ color: "common.white" }}
              aria-label={t("Loading topics")}
            />
          ) : (
            <>
              {otherTopics.map(topic => (
                <Box
                  key={topic.uid}
                  component="button"
                  type="button"
                  onClick={() =>
                    navigate(learnerRoutes.quizPath(), false, {
                      topicUid: topic.uid,
                    })
                  }
                  sx={{
                    fontWeight: 600,
                    borderRadius: "999px",
                    border: "1px solid rgba(255, 255, 255, 0.35)",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    color: "common.white",
                    padding: "5px 14px",
                    fontSize: "1.4rem",
                    cursor: "pointer",
                    userSelect: "none",
                    fontFamily: "inherit",
                    height: "38px",
                    "&:hover": {
                      border: "1px solid rgba(255, 255, 255, 0.5)",
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                    },
                  }}
                >
                  {topic.name}
                </Box>
              ))}
              <Tooltip
                title={t("Complete quizzes to unlock more topics!")}
                describeChild
                open={isUnlockableTopicTooltipOpen}
                onClose={() => {
                  setIsUnlockableTopicTooltipOpen(false);
                }}
                onMouseEnter={() => {
                  setIsUnlockableTopicTooltipOpen(true);
                }}
              >
                <Box
                  component="span"
                  tabIndex={0}
                  onClick={() => {
                    setIsUnlockableTopicTooltipOpen(true);
                  }}
                  sx={{
                    display: "inline-flex",
                    verticalAlign: "middle",
                    cursor: "not-allowed",
                    borderRadius: "999px",
                    height: "38px",
                    outline: "none",
                    "&:focus-visible": {
                      boxShadow: (theme: Theme) =>
                        `0 0 0 2px ${theme.palette.common.white}`,
                    },
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    aria-label={t("Complete quizzes to unlock more topics!")}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      borderRadius: "999px",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      backgroundColor: "rgba(255, 255, 255, 0.12)",
                      color: "rgba(255, 255, 255, 0.45)",
                      padding: "5px 14px",
                      minWidth: 72,
                      minHeight: 36,
                      fontSize: "1.4rem",
                      cursor: "not-allowed",
                      userSelect: "none",
                      fontFamily: "inherit",
                      lineHeight: 1.3,
                      pointerEvents: "none",
                      opacity: 1,
                    }}
                  >
                    <Icon
                      icon={faLockRegular}
                      sx={{
                        fontSize: "1.7rem",
                        color: "inherit",
                        marginRight: "8px",
                      }}
                    />
                    {t("Take a quiz to unlock")}
                  </Box>
                </Box>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function MixupMachineCard() {
  const { t } = useTranslation("learnerApp\\LearnerPlay");
  const goToMixUpMachine = () => navigate(learnerRoutes.mixUpMachinePath());

  const sampleCombinations = [t("Coding + Music"), t("Science + Gaming")];

  return (
    <Box
      flex
      sx={(theme: Theme) => ({
        background:
          "linear-gradient(135deg, rgb(236, 72, 153) 0%, rgb(239, 68, 68) 50%, rgb(245, 158, 11) 100%);",
        borderRadius: "16px",
        padding: "28px 32px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        [theme.breakpoints.down("sm")]: {
          padding: "20px 24px",
        },
      })}
    >
      <Box flex sx={{ alignItems: "center", gap: "16px" }}>
        <Box
          flex
          sx={{
            width: "48px",
            height: "48px",
            borderRadius: "12px",
            backgroundColor: "rgba(255, 255, 255, 0.25)",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "24px",
            flexShrink: 0,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: "white" }}
            data-lov-id="src/pages/ExploreLandingV2Original.tsx:255:18"
            data-lov-name="ShuffleIcon"
            data-component-path="src/pages/ExploreLandingV2Original.tsx"
            data-component-line="255"
            data-component-file="ExploreLandingV2Original.tsx"
            data-component-name="ShuffleIcon"
            data-component-content="%7B%22className%22%3A%22w-5%20h-5%20text-white%22%7D"
          >
            <path d="m18 14 4 4-4 4"></path>
            <path d="m18 2 4 4-4 4"></path>
            <path d="M2 18h1.973a4 4 0 0 0 3.3-1.7l5.454-8.6a4 4 0 0 1 3.3-1.7H22"></path>
            <path d="M2 6h1.972a4 4 0 0 1 3.6 2.2"></path>
            <path d="M22 18h-6.041a4 4 0 0 1-3.3-1.8l-.359-.45"></path>
          </svg>
        </Box>
        <Box flex sx={{ flexDirection: "column", gap: "4px" }}>
          <Typography
            variant="h4"
            sx={{ color: "common.white", fontWeight: 700 }}
          >
            {t("Mix-Up Machine")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "rgba(255, 255, 255, 0.85)" }}
          >
            {t("Select 2 topics and mix them to explore your interests.")}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "1.35rem",
            color: "rgba(255, 255, 255, 0.9)",
            marginRight: "4px",
          }}
        >
          {t("Try")}
        </Typography>
        {sampleCombinations.map(label => (
          <Box
            key={label}
            component="button"
            type="button"
            onClick={goToMixUpMachine}
            sx={{
              fontWeight: 600,
              borderRadius: "999px",
              border: "1px solid rgba(255, 255, 255, 0.35)",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "common.white",
              padding: "5px 14px",
              fontSize: "1.3rem",
              cursor: "pointer",
              userSelect: "none",
              fontFamily: "inherit",
              lineHeight: 1.3,
              "&:hover": {
                border: "1px solid rgba(255, 255, 255, 0.5)",
                backgroundColor: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          onClick={goToMixUpMachine}
          variant="contained"
          color="primary"
          sx={{ fontWeight: 800, fontSize: "1.6rem", padding: "8px 21px" }}
        >
          {t("Start Mixing →")}
        </Button>
      </Box>
    </Box>
  );
}
